import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Paper,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import DownloadIcon from "@mui/icons-material/Download";
import ScheduleIcon from "@mui/icons-material/Schedule";

// Components
import BaseWrapper from "../../components/shared/BaseWrapper";

// Helpers
import { getMinEndDate } from "./utils/payrollHelpers";

// Payroll utils
import {
  calculateNightDifferentialFromSpans,
  sumUniqueMinutesFromSpans,
} from "./utils/payrollUniqueTime";
import { fetchAndSplitJobOrdersForPayroll } from "./utils/PayrollJobOrderFilter";

// API
import { getCacheTechnicians } from "../API/technicianAPI";
import { getJobOrdersForPayroll } from "../API/payrollAPI";

/**
 * Helper: compute totals AND per-day breakdown for one technician
 * using spansByDate and current night settings.
 */
function computeTechTotals(spansByDate, nightStart, nightEnd) {
  const allDates = Object.keys(spansByDate || {}).sort();
  if (allDates.length === 0) {
    return {
      totalUniqueHours: 0,
      totalNightHours: 0,
      totalDayHours: 0,
      perDateBreakdown: {},
    };
  }

  let totalUniqueHours = 0;
  let totalNightHours = 0;
  const perDateBreakdown = {};

  allDates.forEach((date) => {
    const daySpans = spansByDate[date] || [];
    if (!daySpans.length) return;

    // Unique hours for that day (merges overlaps)
    const { hours } = sumUniqueMinutesFromSpans(daySpans);

    // Unique night hours for that day
    const nightHours = calculateNightDifferentialFromSpans(
      daySpans,
      nightStart,
      nightEnd
    );

    const dayHours = Math.max(0, hours - nightHours);

    totalUniqueHours += hours;
    totalNightHours += nightHours;

    perDateBreakdown[date] = {
      dayHours: +dayHours.toFixed(2),
      nightHours: +nightHours.toFixed(2),
      totalHours: +hours.toFixed(2),
    };

    const epsilon = 0.01; // 0.01 hour ≈ 0.6 minutes
    if (Math.abs(dayHours + nightHours - hours) > epsilon) {
      console.warn("Day + night mismatch on", date, {
        hours,
        dayHours,
        nightHours,
      });
    }
  });

  const totalDayHours = Math.max(0, totalUniqueHours - totalNightHours);

  return {
    totalUniqueHours: +totalUniqueHours.toFixed(2),
    totalNightHours: +totalNightHours.toFixed(2),
    totalDayHours: +totalDayHours.toFixed(2),
    perDateBreakdown,
  };
}

/**
 * TechnicianHours page
 */
function TechnicianHours() {
  const [technicians, setTechnicians] = useState([]);
  const [loadingTechs, setLoadingTechs] = useState(false);

  const [searchFilter, setSearchFilter] = useState({
    startDate: "",
    endDate: "",
  });

  const [nightStart, setNightStart] = useState("22:00");
  const [nightEnd, setNightEnd] = useState("06:00");

  // rows = one entry per technician, including totals + per-day breakdown
  const [rows, setRows] = useState([]);
  const [loadingHours, setLoadingHours] = useState(false);

  // BaseWrapper messages
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  // --- Load technicians cache on mount ---
  useEffect(() => {
    const loadTechs = async () => {
      try {
        setLoadingTechs(true);
        setErrorMessage("");
        const techs = await getCacheTechnicians();
        setTechnicians(Array.isArray(techs) ? techs : []);
      } catch (err) {
        console.error("Error loading technicians cache:", err);
        setErrorMessage("Failed to load technicians list.");
      } finally {
        setLoadingTechs(false);
      }
    };

    loadTechs();
  }, []);

  // --- Handlers for date inputs ---
  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setErrorMessage(""); // clear old error
    setSuccessMessage("");
    setSearchFilter((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // when changing night times
  const handleNightStartChange = (e) => {
    setErrorMessage("");
    setSuccessMessage("");
    setNightStart(e.target.value);
  };

  const handleNightEndChange = (e) => {
    setErrorMessage("");
    setSuccessMessage("");
    setNightEnd(e.target.value);
  };

  // --- Main compute action: fetch JOs + compute hours for all techs ---
  const handleSearch = useCallback(async () => {
    if (!searchFilter.startDate || !searchFilter.endDate) {
      setErrorMessage("Please select both start date and end date.");
      return;
    }

    if (!technicians.length) {
      setErrorMessage("No technicians found to compute hours.");
      return;
    }

    if (!nightStart || !nightEnd || nightStart === nightEnd) {
      setErrorMessage(
        "Night period is invalid. Night Start and Night End must be different."
      );
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");
    setLoadingHours(true);

    try {
      const statusFilter = [];
      const serviceReportStatusFilter = [];

      const results = await Promise.all(
        technicians.map(async (tech) => {
          const technicianId =
            tech.id ||
            tech.technician_id ||
            tech.technicianId ||
            tech.employee_id;

          if (!technicianId) {
            return null;
          }

          const technicianName =
            tech.name ||
            tech.technician_name ||
            [tech.first_name, tech.last_name].filter(Boolean).join(" ") ||
            "Unknown Technician";

          const { spansByDate } = await fetchAndSplitJobOrdersForPayroll({
            searchFilter,
            status: statusFilter,
            serviceReportStatus: serviceReportStatusFilter,
            technicianId,
            getJobOrdersApi: getJobOrdersForPayroll,
          });

          const {
            totalUniqueHours,
            totalNightHours,
            totalDayHours,
            perDateBreakdown,
          } = computeTechTotals(spansByDate, nightStart, nightEnd);

          return {
            technicianId,
            technicianName,
            totalUniqueHours,
            totalNightHours,
            totalDayHours,
            perDateBreakdown,
          };
        })
      );

      const cleaned = results
        .filter(Boolean)
        .sort((a, b) => a.technicianName.localeCompare(b.technicianName));

      setRows(cleaned);
      setSuccessMessage("Technician hours calculated successfully.");
    } catch (err) {
      console.error("Error computing technician hours:", err);
      setErrorMessage("Failed to compute technician hours.");
    } finally {
      setLoadingHours(false);
    }
  }, [searchFilter, technicians, nightStart, nightEnd]);

  // --- CSV Export (summary + per-day rows) ---
  const handleExportCsv = () => {
    if (!rows.length) {
      setErrorMessage("No technician hours to export.");
      return;
    }

    setErrorMessage("");
    setSuccessMessage("");

    const now = new Date();
    const reportDate = now.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const reportTime = now.toTimeString().slice(0, 5); // HH:MM

    const safe = (value) => {
      if (value === null || value === undefined) return "";
      const str = String(value);
      return `"${str.replace(/"/g, '""')}"`;
    };

    const lines = [
      "BOAZ Pest Management Corp.",
      "TECHNICIAN HOURS (DETAILED)",
      "",
      `Generated,${reportDate}`,
      `Time,${reportTime}`,
      `Period,${searchFilter.startDate} to ${searchFilter.endDate}`,
      `Night Period,${nightStart} to ${nightEnd}`,
      "",
      "Row Type,Technician ID,Technician Name,Date,Day Hours,Night Hours,Total Work Hours",
    ];

    rows.forEach((row) => {
      const totalDay =
        typeof row.totalDayHours === "number"
          ? row.totalDayHours.toFixed(2)
          : row.totalDayHours || 0;
      const totalNight =
        typeof row.totalNightHours === "number"
          ? row.totalNightHours.toFixed(2)
          : row.totalNightHours || 0;
      const totalWork =
        typeof row.totalUniqueHours === "number"
          ? row.totalUniqueHours.toFixed(2)
          : row.totalUniqueHours || 0;

      // SUMMARY row
      lines.push(
        [
          safe("Summary"),
          safe(row.technicianId),
          safe(row.technicianName),
          safe(""),
          safe(totalDay),
          safe(totalNight),
          safe(totalWork),
        ].join(",")
      );

      // DAILY rows
      const perDateEntries = Object.entries(row.perDateBreakdown || {}).sort(
        ([a], [b]) => a.localeCompare(b)
      );

      perDateEntries.forEach(([date, values]) => {
        lines.push(
          [
            safe("Daily"),
            safe(row.technicianId),
            safe(row.technicianName),
            safe(date),
            safe(values.dayHours.toFixed(2)),
            safe(values.nightHours.toFixed(2)),
            safe(values.totalHours.toFixed(2)),
          ].join(",")
        );
      });

      // blank line between technicians for readability
      lines.push("");
    });

    const csvContent = lines.join("\n");
    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const fileName = `technician-hours_${searchFilter.startDate}_to_${searchFilter.endDate}.csv`;

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    setSuccessMessage("CSV exported successfully.");
  };

  const isBusy = loadingTechs || loadingHours;

  // Optional grand totals
  const grandTotals =
    rows.length > 0
      ? rows.reduce(
          (acc, r) => {
            const day =
              typeof r.totalDayHours === "number" ? r.totalDayHours : 0;
            const night =
              typeof r.totalNightHours === "number" ? r.totalNightHours : 0;
            const work =
              typeof r.totalUniqueHours === "number" ? r.totalUniqueHours : 0;
            acc.day += day;
            acc.night += night;
            acc.work += work;
            return acc;
          },
          { day: 0, night: 0, work: 0 }
        )
      : { day: 0, night: 0, work: 0 };

  return (
    <BaseWrapper
      titleIcon={<ScheduleIcon />}
      title="Payroll"
      subTitle="Technician Hours Summary"
      subTitleRoute={"/technician-hours"}
      successMessage={successMessage}
      setSuccessMessage={setSuccessMessage}
      errorMessage={errorMessage}
      setErrorMessage={setErrorMessage}
      isLoading={isBusy}
    >
      <Box sx={{ mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Select a date range and night period to see total day, night, and work
          hours per technician, with daily breakdowns.
        </Typography>
      </Box>

      {/* Filters: date range + night settings */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
          mb: 3,
        }}
      >
        <TextField
          label="Start Date"
          type="date"
          name="startDate"
          size="small"
          value={searchFilter.startDate}
          onChange={handleDateChange}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End Date"
          type="date"
          name="endDate"
          size="small"
          value={searchFilter.endDate}
          inputProps={{
            min: getMinEndDate(searchFilter.startDate),
          }}
          onChange={(e) => {
            const newEndDate = e.target.value;
            if (
              !searchFilter.startDate ||
              newEndDate >= searchFilter.startDate
            ) {
              setSearchFilter((prev) => ({
                ...prev,
                endDate: newEndDate,
              }));
            }
          }}
          InputLabelProps={{ shrink: true }}
        />

        <TextField
          label="Night Start"
          type="time"
          size="small"
          value={nightStart}
          onChange={handleNightStartChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }} // 5-minute steps
        />
        <TextField
          label="Night End"
          type="time"
          size="small"
          value={nightEnd}
          onChange={handleNightEndChange}
          InputLabelProps={{ shrink: true }}
          inputProps={{ step: 300 }}
        />

        <Button
          variant="contained"
          onClick={handleSearch}
          disabled={
            isBusy ||
            !searchFilter.startDate ||
            !searchFilter.endDate ||
            !technicians.length
          }
        >
          {loadingHours ? "Calculating..." : "Search"}
        </Button>

        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleExportCsv}
          disabled={isBusy || !rows.length}
          sx={{ textTransform: "none" }}
        >
          Download CSV
        </Button>
      </Box>

      {!loadingTechs && !technicians.length && (
        <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
          No technicians found. Please check your technician list / cache API.
        </Typography>
      )}

      {isBusy && (
        <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">
            {loadingTechs ? "Loading technicians..." : "Calculating hours..."}
          </Typography>
        </Box>
      )}

      {/* Results: one accordion per technician */}
      <Box>
        {rows.length === 0 ? (
          <Paper sx={{ p: 2, textAlign: "center" }}>
            <Typography variant="body2">
              {isBusy
                ? "Processing..."
                : "No data. Select a date range and click Search."}
            </Typography>
          </Paper>
        ) : (
          rows.map((row) => {
            const perDateEntries = Object.entries(
              row.perDateBreakdown || {}
            ).sort(([a], [b]) => a.localeCompare(b));

            return (
              <Accordion key={row.technicianId} sx={{ mb: 1 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      width: "100%",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    {/* Left: Technician info */}
                    <Box>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {row.technicianName}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        ID: {row.technicianId}
                      </Typography>
                    </Box>

                    {/* Right: Totals */}
                    <Box
                      sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 3,
                        ml: "auto",
                      }}
                    >
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Total Day Hours
                        </Typography>
                        <Typography variant="body1">
                          {typeof row.totalDayHours === "number"
                            ? row.totalDayHours.toFixed(2)
                            : row.totalDayHours}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Total Night Hours
                        </Typography>
                        <Typography variant="body1">
                          {typeof row.totalNightHours === "number"
                            ? row.totalNightHours.toFixed(2)
                            : row.totalNightHours}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: "right" }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          display="block"
                        >
                          Total Work Hours
                        </Typography>
                        <Typography variant="body1">
                          {typeof row.totalUniqueHours === "number"
                            ? row.totalUniqueHours.toFixed(2)
                            : row.totalUniqueHours}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  {perDateEntries.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1 }}
                    >
                      No work hours found for this technician in the selected
                      range.
                    </Typography>
                  ) : (
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Date</TableCell>
                            <TableCell align="right">Day Hours</TableCell>
                            <TableCell align="right">Night Hours</TableCell>
                            <TableCell align="right">
                              Total Work Hours
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {perDateEntries.map(([date, values]) => (
                            <TableRow key={date}>
                              <TableCell>{date}</TableCell>
                              <TableCell align="right">
                                {values.dayHours.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {values.nightHours.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {values.totalHours.toFixed(2)}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      {/* Grand totals for all technicians */}
      {rows.length > 0 && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            borderRadius: 1,
            border: "1px solid #e0e0e0",
            display: "flex",
            flexWrap: "wrap",
            gap: 4,
          }}
        >
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Total Day Hours (All Techs)
            </Typography>
            <Typography variant="subtitle1">
              {grandTotals.day.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Total Night Hours (All Techs)
            </Typography>
            <Typography variant="subtitle1">
              {grandTotals.night.toFixed(2)}
            </Typography>
          </Box>
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
            >
              Total Work Hours (All Techs)
            </Typography>
            <Typography variant="subtitle1">
              {grandTotals.work.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      )}
    </BaseWrapper>
  );
}

export default TechnicianHours;
